import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { formatInteger } from '../../../utils/formatting'
import { DEAL_TABLE_KEY } from 'constants/tableQueryKeys'
import { Tabs } from 'components/Tabs'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import useApiService from 'services/api'
import { DealCard } from 'components/NewCompanyPage/DealCard'
import { DealReport } from 'models/deals_reports'
import { useAuth } from 'context/authentication/useAuth'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import { MobileEditDealModal } from 'components/Deals/MobileEditDealModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import {
  BackButton,
  PageContainer,
} from 'components/MobileSearch/MobileSearch.styles'

const sortingOptions = [
  {
    label: <SortingOptionLabel label="Last Updated" type="number" />,
    value: 'modified',
  },
  {
    label: <SortingOptionLabel label="Last Updated" type="number" desc />,
    value: '-modified',
  },
  {
    label: <SortingOptionLabel label="Date Added" type="number" />,
    value: 'created',
  },
  {
    label: <SortingOptionLabel label="Date Added" type="number" desc />,
    value: '-created',
  },
  {
    label: <SortingOptionLabel label="Company" />,
    value: 'cc_company_name',
  },
  {
    label: <SortingOptionLabel label="Company" desc />,
    value: '-cc_company_name',
  },
  {
    label: <SortingOptionLabel label="Sales Stage" />,
    value: 'sales_stage_name',
  },
  {
    label: <SortingOptionLabel label="Sales Stage" desc />,
    value: '-sales_stage_name',
  },
]

export function MobileDealsPage() {
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState<string>('my_deals')
  const [searchParams] = useSearchParams()
  const [dealIdToEdit, setDealIdToEdit] = useState<number>()
  const api = useApiService()
  const { user, isLimited } = useAuth()

  const IDENTIFIER = 'DEALS_TABLE'

  const dealsFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filterStore = dealsFilterStore()
  const { page, setPage } = filterStore

  // Read chain_id from URL query params
  const filterIdFromUrl = searchParams.get('contact_company_id')

  const { data: dealsCounts } = api.useGetDealsCounts(
    filterIdFromUrl ? { contact_company_id: filterIdFromUrl } : {}
  )

  const handleBack = () => {
    navigate(-1)
  }

  // Fetch sales stages
  const { data: salesStages } = api.useGetSalesStages()

  // Fetch users for account owner names
  const { data: users } = api.useGetUsers(isLimited)

  const sortingStore = dataTableSortingStoreRepo.getStore(DEAL_TABLE_KEY)()

  const { sorting } = sortingStore

  const filtersAsParams = {
    ...useFilterParams(dealsFilterStore),
  }

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    }
  }, [selectedTab])

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }
    return params
  }, [filtersAsParams])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
      ...(selectedTab === 'my_deals' ? { account_owner_id: user?.id } : {}),
      ...(filterIdFromUrl ? { company: filterIdFromUrl } : {}),
    }),
    [filterParams, sortParams, selectedTab, filterIdFromUrl]
  )

  const handleEditDeal = (deal: DealReport) => {
    setDealIdToEdit(deal.id)
  }

  const [query, queryKey] = api.useGetReportDealsInfinite({
    page,
    page_size: 10,
    ...filterAndSortParams,
  })

  return (
    <PageContainer>
      {filterIdFromUrl && (
        <BackButton onClick={handleBack}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
      )}

      <div className="relative mt-3 mx-2">
        <Tabs
          tabs={[
            {
              key: 'my_deals',
              title: `My Deals (${formatInteger(dealsCounts?.my_deals_count)})`,
            },
            {
              key: 'all_deals',
              title: `All Deals (${formatInteger(dealsCounts?.all_deals_count)})`,
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>

      <MobileListViewHeader
        sortingOptions={sortingOptions}
        filterStoreIdentifier={IDENTIFIER}
        tableKey={DEAL_TABLE_KEY}
        defaultSorting={[{ id: 'modified', desc: true }]}
      />

      <FilterChips
        store={dealsFilterStore}
        identifier={IDENTIFIER}
        clearAllButton
        showActive
      />

      <MobileInfiniteScrollView<{ results: DealReport[] }, DealReport>
        extractItems={(page) => page.results}
        infiniteQuery={query}
      >
        {(deals) => {
          return deals?.map((deal: DealReport) => (
            <DealCard
              key={deal.id}
              deal={deal}
              salesStages={salesStages}
              users={users}
              onEdit={handleEditDeal}
            />
          ))
        }}
      </MobileInfiniteScrollView>

      {!!dealIdToEdit && (
        <MobileEditDealModal
          onClose={() => setDealIdToEdit(undefined)}
          dealsTableQueryKey={queryKey}
          show={!!dealIdToEdit}
          dealId={dealIdToEdit}
        />
      )}
    </PageContainer>
  )
}
